import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import AuthenticatedNavigation from '../../components/navigation/AuthenticatedNavigation';
import CandidateCard from './components/CandidateCard';
import FilterPanel from './components/FilterPanel';
import SubscriptionPanel from './components/SubscriptionPanel';
import AnalyticsPanel from './components/AnalyticsPanel';
import RevealRequestModal from './components/RevealRequestModal';
import CandidateDetailModal from './components/CandidateDetailModal';
import JobDescriptionUpload from './components/JobDescriptionUpload';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { batchAnalyzeCandidates } from '../../services/matchingService';
import { parseJobDescription } from '../../services/jobDescriptionService';
import { candidatesService } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const EmployerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [revealModalCandidate, setRevealModalCandidate] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [matchAnalyses, setMatchAnalyses] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [jobOpportunity, setJobOpportunity] = useState(null);
  const [isParsingJob, setIsParsingJob] = useState(false);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [filters, setFilters] = useState({
    keywords: '',
    location: 'all',
    industry: 'all',
    seniority: 'all',
    management: 'all',
    experienceMin: '',
    experienceMax: ''
  });

  // Load candidates from Supabase
  useEffect(() => {
    const loadCandidates = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await candidatesService?.getAll();
        setCandidates(data || []);
        setFilteredCandidates(data || []);
      } catch (err) {
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };

    loadCandidates();
  }, [user]);

  useEffect(() => {
    if (jobOpportunity && candidates?.length > 0) {
      analyzeAllCandidates();
    }
  }, [jobOpportunity, candidates]);

  const analyzeAllCandidates = async () => {
    setIsAnalyzing(true);
    try {
      const analyses = await batchAnalyzeCandidates(candidates, jobOpportunity);
      const analysisMap = {};
      analyses?.forEach(analysis => {
        analysisMap[analysis.candidateId] = analysis;
      });
      setMatchAnalyses(analysisMap);
    } catch (error) {
      console.error('Failed to analyze candidates:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getCandidateWithMatchScore = (candidate) => {
    const analysis = matchAnalyses?.[candidate?.id];
    if (analysis && !analysis?.error) {
      return {
        ...candidate,
        matchScore: Math.round(analysis?.matchPercentage),
        matchAnalysis: analysis
      };
    }
    return candidate;
  };

  const mockSubscription = {
    plan: "Professional",
    revealsTotal: 50,
    revealsUsed: 38,
    activeReveals: 12,
    pendingReveals: 5,
    nextBilling: "02/15/2026"
  };

  const mockAnalytics = {
    profileViews: 1247,
    profileViewsChange: 12.5,
    revealRequests: 38,
    revealRequestsChange: 8.3,
    approvedMatches: 12,
    approvedMatchesChange: -5.2,
    scheduledInterviews: 7,
    scheduledInterviewsChange: 16.7
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      keywords: '',
      location: 'all',
      industry: 'all',
      seniority: 'all',
      management: 'all',
      experienceMin: '',
      experienceMax: ''
    });
  };

  const handleRevealRequest = (candidate) => {
    setRevealModalCandidate(candidate);
  };

  const handleViewDetails = (candidate) => {
    setSelectedCandidate(candidate);
  };

  const handleSubmitRevealRequest = async (candidateId, message) => {
    // Mock implementation for reveal request submission
    const employerId = user?.id || 'demo_employer';
    
    // Simulate reveal request creation
    console.log('Reveal request created:', {
      candidateId,
      employerId,
      message,
      matchId: `match_${candidateId}_${employerId}`
    });
    // End of added block
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    setRevealModalCandidate(null);
    alert('Reveal request submitted successfully! You will be notified once approved.');
  };

  const handleJobDescriptionUpload = async (jobDescriptionText) => {
    setIsParsingJob(true);
    try {
      const parsedJob = await parseJobDescription(jobDescriptionText);
      setJobOpportunity(parsedJob);
    } catch (error) {
      console.error('Failed to parse job description:', error);
      alert(error?.message || 'Failed to analyze job description. Please try again.');
    } finally {
      setIsParsingJob(false);
    }
  };

  // Add this block - Filter candidates based on filters state
  useEffect(() => {
    const applyFilters = () => {
      let filtered = [...candidates];

      if (filters?.keywords) {
        const keywords = filters?.keywords?.toLowerCase();
        filtered = filtered?.filter(candidate =>
          candidate?.title?.toLowerCase()?.includes(keywords) ||
          candidate?.summary?.toLowerCase()?.includes(keywords) ||
          candidate?.skills?.some(skill => skill?.toLowerCase()?.includes(keywords))
        );
      }

      if (filters?.location !== 'all') {
        filtered = filtered?.filter(candidate =>
          candidate?.location?.toLowerCase()?.includes(filters?.location?.toLowerCase())
        );
      }

      if (filters?.industry !== 'all') {
        filtered = filtered?.filter(candidate =>
          candidate?.industry?.toLowerCase() === filters?.industry?.toLowerCase()
        );
      }

      if (filters?.seniority !== 'all') {
        filtered = filtered?.filter(candidate =>
          candidate?.seniority?.toLowerCase() === filters?.seniority?.toLowerCase()
        );
      }

      if (filters?.management !== 'all') {
        filtered = filtered?.filter(candidate =>
          candidate?.managementLevel?.toLowerCase() === filters?.management?.toLowerCase()
        );
      }

      if (filters?.experienceMin) {
        const minExp = parseInt(filters?.experienceMin);
        filtered = filtered?.filter(candidate => {
          const exp = parseInt(candidate?.yearsOfExperience || candidate?.experience);
          return exp >= minExp;
        });
      }

      if (filters?.experienceMax) {
        const maxExp = parseInt(filters?.experienceMax);
        filtered = filtered?.filter(candidate => {
          const exp = parseInt(candidate?.yearsOfExperience || candidate?.experience);
          return exp <= maxExp;
        });
      }

      const filteredWithScores = filtered?.map(getCandidateWithMatchScore);
      setFilteredCandidates(filteredWithScores);
    };

    applyFilters();
  }, [filters, candidates, matchAnalyses]);
  // End of added block

  return (
    <>
      <Helmet>
        <title>Employer Dashboard - BlinD</title>
        <meta name="description" content="Discover and connect with exceptional senior talent through anonymous profiles. Access curated candidates and manage your hiring pipeline." />
      </Helmet>
      <div className="min-h-screen bg-[#F6F7FB]">
        <AuthenticatedNavigation userRole="employer" />

        <div className="lg:pl-60 pb-16 lg:pb-0">
          <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#111827] mb-2">
                Candidate Discovery
              </h1>
              <p className="text-base md:text-lg text-[#6B7280]">
                Browse anonymous profiles and connect with exceptional senior talent
              </p>
            </div>

            <AnalyticsPanel analytics={mockAnalytics} />

            <div className="mt-6 md:mt-8">
              <JobDescriptionUpload 
                onJobParsed={handleJobDescriptionUpload}
                isProcessing={isParsingJob || isAnalyzing}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8 mt-6 md:mt-8">
              <div className="lg:col-span-1">
                <div className="space-y-6">
                  <FilterPanel
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    isOpen={isFilterOpen}
                    onToggle={() => setIsFilterOpen(!isFilterOpen)}
                  />
                  <div className="hidden lg:block">
                    <SubscriptionPanel
                      subscription={mockSubscription}
                      onUpgrade={() => alert('Upgrade functionality coming soon!')}
                    />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 md:p-6 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon name="Users" size={24} color="#5B2DFF" />
                      <div>
                        <h2 className="text-lg md:text-xl font-semibold text-[#111827]">
                          {filteredCandidates?.length} Candidates Found
                        </h2>
                        <p className="text-sm text-[#6B7280]">
                          Showing results based on your filters
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="default"
                      iconName="SlidersHorizontal"
                      iconPosition="left"
                      onClick={() => setIsFilterOpen(true)}
                      className="lg:hidden"
                    >
                      Filters
                    </Button>
                  </div>
                </div>

                <div className="space-y-4 md:space-y-6">
                  {filteredCandidates?.map(candidate => (
                    <CandidateCard
                      key={candidate?.id}
                      candidate={candidate}
                      onRevealRequest={handleRevealRequest}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>

                {filteredCandidates?.length === 0 && (
                  <div className="bg-white rounded-2xl border border-[#E5E7EB] p-8 md:p-12 text-center">
                    <Icon name="Search" size={48} color="#6B7280" className="mx-auto mb-4" />
                    <h3 className="text-lg md:text-xl font-semibold text-[#111827] mb-2">
                      No candidates found
                    </h3>
                    <p className="text-sm md:text-base text-[#6B7280] mb-6">
                      Try adjusting your filters to see more results
                    </p>
                    <Button
                      variant="outline"
                      iconName="RotateCcw"
                      iconPosition="left"
                      onClick={handleClearFilters}
                    >
                      Clear All Filters
                    </Button>
                  </div>
                )}

                <div className="lg:hidden mt-6">
                  <SubscriptionPanel
                    subscription={mockSubscription}
                    onUpgrade={() => alert('Upgrade functionality coming soon!')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {revealModalCandidate && (
        <RevealRequestModal
          candidate={revealModalCandidate}
          onClose={() => setRevealModalCandidate(null)}
          onSubmit={handleSubmitRevealRequest}
        />
      )}
      {selectedCandidate && (
        <CandidateDetailModal
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
          onRevealRequest={handleRevealRequest}
        />
      )}
    </>
  );
};

export default EmployerDashboard;